import React from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function DayEndScreen({ send }: Props) {
  const { currentDay, playerName, role, isHost, players, setPhase } = useGameStore();

  // Mock scores for now
  const myDayScore = Math.floor(Math.random() * 100) + 200;
  const cumulativeScore = myDayScore * currentDay;

  // Generate leaderboard (mock data mixed with real players)
  const leaderboard = players
    .map(p => ({
      ...p,
      score: Math.floor(Math.random() * 150) + 200,
    }))
    .sort((a, b) => b.score - a.score);

  const myRank = leaderboard.findIndex(p => p.playerName === playerName) + 1;

  const handleNextDay = () => {
    if (currentDay >= 7) {
      setPhase('game_end');
    } else {
      send('startNextDay', {});
      setPhase('gameplay');
    }
  };

  return (
    <div className="screen">
      <div style={{ textAlign: 'center', width: '100%', maxWidth: 700 }}>
        <h1 className="title-pixel" style={{ fontSize: 28, marginBottom: 8 }}>
          Day {currentDay} Complete!
        </h1>
        <p className="subtitle" style={{ marginBottom: 32 }}>
          {currentDay >= 7 ? 'Final day completed!' : `${7 - currentDay} days remaining`}
        </p>

        {/* Score Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 32,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white',
          }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Today's Score</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>{myDayScore}</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white',
          }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Total Score</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>{cumulativeScore}</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white',
          }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Your Rank</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>#{myRank}</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            padding: 20,
            borderRadius: 12,
            color: 'white',
          }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              {role === 'chef' ? 'Dishes Cooked' : 'Orders Placed'}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>4</div>
          </div>
        </div>

        {/* Leaderboard */}
        <div style={{
          background: 'var(--ltc-bg-card)',
          border: 'var(--ltc-border)',
          marginBottom: 32,
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--ltc-primary)',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            fontWeight: 600,
          }}>
            <div style={{ width: 50 }}>Rank</div>
            <div style={{ flex: 1 }}>Player</div>
            <div style={{ width: 100, textAlign: 'right' }}>Score</div>
          </div>
          {leaderboard.slice(0, 5).map((player, idx) => (
            <div key={player.playerId} style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              background: player.playerName === playerName ? '#ffe8d6' : (idx % 2 ? '#f9f9f9' : 'white'),
              borderBottom: '1px solid #eee',
            }}>
              <div style={{ width: 50, fontWeight: 700, fontSize: 18 }}>
                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`}
              </div>
              <div style={{ flex: 1, fontWeight: player.playerName === playerName ? 600 : 400 }}>
                {player.playerName}
                {player.playerName === playerName && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ltc-accent)' }}>
                    (You)
                  </span>
                )}
              </div>
              <div style={{ 
                width: 100, 
                textAlign: 'right', 
                fontWeight: 700,
                color: 'var(--ltc-primary)',
              }}>
                {player.score}
              </div>
            </div>
          ))}
        </div>

        {/* Performance Breakdown */}
        <div style={{
          background: 'var(--ltc-bg-secondary)',
          padding: 20,
          border: 'var(--ltc-border)',
          marginBottom: 32,
          textAlign: 'left',
        }}>
          <h4 style={{ marginBottom: 16, color: 'var(--ltc-primary)' }}>
            📊 Your Performance Breakdown
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><strong>Breakfast:</strong> {Math.floor(myDayScore * 0.3)} pts</div>
            <div><strong>Lunch:</strong> {Math.floor(myDayScore * 0.25)} pts</div>
            <div><strong>Snack:</strong> {Math.floor(myDayScore * 0.2)} pts</div>
            <div><strong>Dinner:</strong> {Math.floor(myDayScore * 0.25)} pts</div>
          </div>
          <div style={{ 
            marginTop: 16, 
            paddingTop: 16, 
            borderTop: '2px dashed var(--ltc-border-color)',
          }}>
            <strong>Avg Rating:</strong> ⭐⭐⭐⭐ (4.2/5.0)
          </div>
        </div>

        {/* Continue Button */}
        {isHost ? (
          <button
            className="btn btn-accent btn-large"
            onClick={handleNextDay}
            style={{ minWidth: 250 }}
          >
            {currentDay >= 7 ? '🏆 View Final Results' : `Continue to Day ${currentDay + 1} →`}
          </button>
        ) : (
          <div style={{
            background: 'var(--ltc-bg-secondary)',
            padding: 16,
            color: 'var(--ltc-text-muted)',
          }}>
            ⏳ Waiting for host to continue...
          </div>
        )}
      </div>
    </div>
  );
}
